/**
 * Button.jsx — Project-wide reusable button component
 *
 * Variants:  primary | outline | danger | orange | secondary | soft | ghost
 * Sizes:     sm | md | lg
 * Props:     wide, pill, icon, iconRight, disabled, loading, className, onClick
 *
 * All colors come from AVA design-token CSS variables (globals.css).
 * No hardcoded hex values anywhere in this file.
 *
 * Usage:
 *   import Button, { BtnContinue, BtnBack, BtnAskNavigator } from '../Reusablesteps/Button';
 *
 *   <Button variant="primary" onClick={onNext}>Continue</Button>
 *   <Button variant="outline" onClick={onBack}>← Back</Button>
 *   <Button variant="danger"  wide onClick={onNext}>⚠️ Continue</Button>
 *   <Button variant="soft"    pill icon="💬">Ask a Navigator</Button>
 *   <Button variant="primary" loading>Saving…</Button>
 */


import React from 'react';
import styles from './button.module.css';

/* ─── Spinner ────────────────────────────────────────────────────────────── */
function Spinner({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      className={styles.spinner}
    >
      <circle cx="12" cy="12" r="10" strokeOpacity={0.25} />
      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
    </svg>
  );
}

/* ─── Button ─────────────────────────────────────────────────────────────── */
export default function Button({
  variant   = 'primary',
  size      = 'md',
  wide      = false,
  pill      = false,
  icon,
  iconRight,
  disabled  = false,
  loading   = false,
  className = '',
  onClick,
  children,
  style: extraStyle,
  type = 'button',
  ...rest
}) {
  const isOff = disabled || loading;

  const cls = [
    styles.btn,
    styles[variant] || styles.primary,
    styles[size]    || styles.md,
    wide    ? styles.wide    : '',
    pill    ? styles.pill    : '',
    loading ? styles.loading : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /* Icon size mapped to button size */
  const iconSize = size === 'sm' ? 12 : size === 'lg' ? 16 : 14;

  return (
    <button
      {...rest}
      type={type}
      disabled={isOff}
      className={cls}
      style={extraStyle}
      onClick={isOff ? undefined : onClick}
    >
      {/* Left icon or loading spinner */}
      {loading ? (
        <Spinner size={iconSize} />
      ) : icon ? (
        <span className={styles.iconLeft}>{icon}</span>
      ) : null}

      {/* Label */}
      {children}

      {/* Right icon (arrow, chevron, etc.) */}
      {!loading && iconRight && (
        <span className={styles.iconRight}>{iconRight}</span>
      )}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Pre-composed shortcuts
   Drop-in replacements for the old inline-styled components.
   Import individually wherever needed.
───────────────────────────────────────────────────────────────────────── */

/**
 * Replaces <BtnContinue> across all CareJourney screens.
 * color prop kept for backward-compat: red hex → danger variant.
 */
export function BtnContinue({
  onClick,
  disabled,
  label = 'Continue',
  wide  = false,
  lock,
  color,
}) {
  const variant = color === '#EF4444' ? 'danger' : 'primary';
  const ArrowRight = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} fill="none" stroke={color} strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);
const Lock = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} fill="none" stroke={color} strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);
  return (
    <Button variant={variant} size="md"  disabled={disabled} onClick={onClick}>
      {/* {lock?<Lock size={14} color="white" />:""} */}
      {label}
       {/* <ArrowRight size={15} color="white" /> */}
    </Button>
  );
}

/** Replaces <BtnBack> across all CareJourney screens. */
export function BtnBack({ onClick }) {
    const ArrowLeft = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} fill="none" stroke={color} strokeWidth={2.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
  </svg>
);
  return (
    <Button variant="outline" size="md" onClick={onClick}>
      {/* <ArrowLeft size={15} />  */}
      Back
    </Button>
  );
}

/** Replaces <BtnAskNavigator>. */
export function BtnAskNavigator({ onClick }) {
  return (
    <Button variant="soft" size="md" pill icon="💬" onClick={onClick}>
      Ask a Care Navigator
    </Button>
  );
}